// Google Analytics Configuration
// Replace 'G-XXXXXXXXXX' with your actual Google Analytics 4 Measurement ID
// Get your ID from: https://analytics.google.com/

export const GA_MEASUREMENT_ID = 'G-4BXB1W38HP';

// Google Ads account
export const GOOGLE_ADS_ID = 'AW-18164126524';

// Conversion label map. Replace the empty strings with your conversion
// labels from Google Ads (Tools → Conversions → tag setup).
// Each label looks like 'AbCdEfGhIjKlMnOp'.
// Until a label is set, the corresponding event becomes a no-op.
export const ADS_CONVERSIONS: Record<string, string> = {
  quote_form_submit: '3HkSCJXUra0CELymqtVD',
  contact_form_submit: '3HkSCJXUra0CELymqtVD',
  floating_form_submit: '3HkSCJXUra0CELymqtVD',
  brochure_form_submit: '3HkSCJXUra0CELymqtVD',
  whatsapp_click: '3HkSCJXUra0CELymqtVD',
  phone_click: '3HkSCJXUra0CELymqtVD',
};

// Map internal action names to Meta Pixel standard events so leads show up
// in Events Manager under recognized categories.
const META_PIXEL_EVENTS: Record<string, string> = {
  quote_form_submit: 'Lead',
  contact_form_submit: 'Lead',
  floating_form_submit: 'Lead',
  brochure_form_submit: 'Lead',
  whatsapp_click: 'Contact',
  phone_click: 'Contact',
};

// Fire a Meta Pixel event. Safe no-op if fbq hasn't loaded yet.
export const trackMetaPixel = (
  event: string,
  params?: Record<string, any>,
  options?: { custom?: boolean }
) => {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') return;
  try {
    window.fbq(options?.custom ? 'trackCustom' : 'track', event, params);
    if (import.meta.env.DEV) {
      console.log('[meta-pixel]', event, params);
    }
  } catch (err) {
    console.warn('Meta Pixel error:', err);
  }
};

let gaLoaded = false;
let gaLoadPromise: Promise<void> | null = null;

// Lazy load Google Analytics - only loads on user interaction or after delay
const loadGA = (): Promise<void> => {
  if (gaLoaded) {
    return Promise.resolve();
  }

  if (gaLoadPromise) {
    return gaLoadPromise;
  }

  gaLoadPromise = new Promise((resolve) => {
    if (!GA_MEASUREMENT_ID || !GA_MEASUREMENT_ID.startsWith('G-') || GA_MEASUREMENT_ID.includes('XXXX')) {
      console.warn('⚠️ Google Analytics: Invalid Measurement ID');
      resolve();
      return;
    }

    // Initialize dataLayer before script loads
    window.dataLayer = window.dataLayer || [];
    function gtag(...args: any[]) {
      window.dataLayer.push(args);
    }
    window.gtag = gtag;

    // Load gtag script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    
    script.onload = () => {
      gtag('js', new Date());
      gtag('config', GA_MEASUREMENT_ID, {
        send_page_view: false, // We'll send it manually
      });
      gaLoaded = true;
      console.log('📊 Google Analytics loaded lazily');
      resolve();
    };

    script.onerror = () => {
      console.warn('Failed to load Google Analytics');
      resolve();
    };

    document.head.appendChild(script);
  });

  return gaLoadPromise;
};

// Initialize Google Analytics with lazy loading facade
export const initGA = () => {
  // Load on user interaction (scroll, click, mousemove, touchstart)
  const events = ['scroll', 'click', 'mousemove', 'touchstart', 'keydown'];
  const loadOnInteraction = () => {
    loadGA();
    events.forEach(event => window.removeEventListener(event, loadOnInteraction));
  };

  events.forEach(event => {
    window.addEventListener(event, loadOnInteraction, { once: true, passive: true });
  });

  // Fallback: Load after 5 seconds if no interaction
  setTimeout(() => {
    if (!gaLoaded) {
      loadGA();
    }
  }, 5000);
};

// Track page views - loads GA if not loaded yet
export const trackPageView = async (url: string) => {
  await loadGA();
  if (typeof window.gtag !== 'undefined') {
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: url,
    });
  }
};

// Track custom events - loads GA if not loaded yet
export const trackEvent = async (eventName: string, parameters?: Record<string, any>) => {
  await loadGA();
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', eventName, parameters);
  }
};

// Fire a Google Ads conversion. `action` must match a key in ADS_CONVERSIONS.
// Optionally include `value` and `currency` for value-based conversions.
export const trackConversion = async (
  action: keyof typeof ADS_CONVERSIONS,
  options?: { value?: number; currency?: string; transaction_id?: string }
) => {
  const label = ADS_CONVERSIONS[action];
  if (!label) {
    if (import.meta.env.DEV) {
      console.warn(`[ads] No conversion label set for "${action}" — skipping.`);
    }
    return;
  }
  // gtag.js is loaded synchronously in index.html, so window.gtag is available
  // immediately. Fall back to dataLayer.push if gtag isn't ready yet so we
  // never lose a conversion event.
  const payload = {
    send_to: `${GOOGLE_ADS_ID}/${label}`,
    ...(options?.value !== undefined ? { value: options.value } : {}),
    currency: options?.currency || 'SAR',
    ...(options?.transaction_id ? { transaction_id: options.transaction_id } : {}),
  };
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'conversion', payload);
  } else {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(['event', 'conversion', payload]);
  }
  if (import.meta.env.DEV) {
    console.log('[ads] conversion fired:', action, payload);
  }

  // Mirror to Meta Pixel as a standard event for Events Manager.
  const metaEvent = META_PIXEL_EVENTS[action];
  if (metaEvent) {
    trackMetaPixel(metaEvent, {
      content_name: action,
      ...(options?.value !== undefined ? { value: options.value } : {}),
      currency: options?.currency || 'SAR',
    });
  }
};

declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
    fbq: (...args: any[]) => void;
  }
}
